import { ENV_VARS } from '@env';
import { isString, toNumber } from 'lodash-es';
/** These are all of the feature flag ids that the frontend is concerned with. If you are adding a brand new flag, the id will have to be appended to this list
 **/
import { JsonObject } from 'type-fest';
import { BoolFlagKeys, NumberFlagKeys, StringFlagKeys } from './data';
import { JsonFlagKeys } from './jsonFlags';

export type NamesWithoutJson = BoolFlagKeys | NumberFlagKeys | StringFlagKeys;

// prettier-ignore
export type FlagsObject = {
    [k in BoolFlagKeys]: boolean;
  } & {
    [k in StringFlagKeys]: string;
  } & {
    [k in JsonFlagKeys]: JsonObject;
  } & {
    [k in NumberFlagKeys]: number;
  };

/** These defaults only get evaluated when USE_LD is false. They are not used for production environments. Flags will automatically be true unless otherwise defined below */
export const flagDefaultsForLocalDev: Partial<FlagsObject> &
  Partial<{
    // Bool flags default to true, so adding true is redundant.
    [k in BoolFlagKeys]: false;
  }> = {
  'app-show-offline-message': false,
  'app-keycloak-min-validity-seconds': 30000,
  'app-keycloak-trigger-refresh-seconds': 240,
  'feat-list-loads-limit': 5,
  'home-pending-received-page-limit': 12,
  'ME-10932-view-home-mytask-task-service': false,
  'ME-11055-feat-trailer-id-validation': false,
  'route-lock-polling-interval-ms': isString(ENV_VARS.VITE_POLL_INTERVAL_MS)
    ? toNumber(ENV_VARS.VITE_POLL_INTERVAL_MS)
    : 30_000,
  'route-lock-expiration-warning-modal-seconds': 30,
  'temp-restrict-molo-stop-times': false,
  'accounting-page-limit': 50,
  'offers-board-routes-batch-size': 300,
  'track-page-auto-polling-ms': 0,
  'ME-14627-view-add-offer-with-response': {},
  'ME-11793-app-portfolio-pagination-number': 100,
  'ME-8932-feat-booking-events-subscription': false,
  'ME-18357-feat-route-vendor-details-v2': false,
  'ME-18200-feat-table-context-menu-config': {
    exclude: [],
  } as fixMe,
  'ME-19760-view-rate-con-section-v2': false,
  'ME-12577-view-autocomplete-address': false,
  'ME-14873-feat-distance-details': false,
  'ME-22735-fix-facility-picker-query-limit-number': 200,
  'ME-17877-feat-table-column-resize-exclude': {
    exclude: [
      'main-routes-open',
      'main-routes-open-asl',
      'main-routes-covered-asl',
      'main-routes-dispatched-asl',
      'main-routes-arrived-origin-asl',
      'main-routes-in-transit-asl',
      'main-routes-covered',
      'main-routes-dispatched',
    ],
  } as fixMe,
  'ME-22379-feat-appointment-change-reason': {},
  'ME-31132-view-main-page-route-board-pagination-size-number': 400,
  'ME-30981-view-track-page-pagination-size-number': 400,
  'ME-31924-view-load-search-pagination-size-number': 400,
  'ME-33595-view-matches-without-offers': false,
  'ME-31432-app-main-page-route-board-polling-ms': 18000,
  'ME-44282-view-record-management-tracking-polling-ms': 18000,
  'ME-54280-app-table-auto-virtualize-above-row-number': 50,
  'ME-35600-feat-masterfind-debounce-ms': 400,
  'ME-35786-feat-employee-migration-v2': {
    localhost: false,
  },
  'ME-35786-feat-customer-migration-v2': {
    localhost: false,
  },
  'ME-35786-feat-carrier-migration-v2': {
    localhost: false,
  },
  'ME-35786-feat-facility-migration-v2': {
    localhost: false,
  },
  'ME-23869-feat-Post-Details-Modal-BIN-price': false,
  'ME-35876-feat-booked-by-hyperlink': false,
  // prettier-ignore
  // eslint-disable-next-line no-useless-escape
  'ME-32857-app-masterfind-regex': '_x_',
  'ME-31848-feat-remove-stop-event-validation-for-edi-testing': false,
  'ME-49548-view-load-new-tabs': false,
  'ME-56282-feat-customer-crm': false,
  'ME-49695-feat-use-recordsV2': false,
  'ME-58954-feat-open-deck-fields': false,
  'ME-56328-feat-open-deck-drawer-fields': false,
  'ME-58578-feat-use-geographiesRD': false,
  'ME-61696-view-enable-electronic-trackings-table': false,
  'ME-61696-view-enable-carrier-groups-table': false,
  'ME-61696-view-enable-multiselect-employee-groups': false,
  'ME-57583-view-facility-scheduling-size-dropdown-adjustments': false,
  'ME-52933-feat-use-plan-loadV2': false,
  'ME-56363-feat-ROR-decommision': false,
  'ME-61741-view-slack-share': false,
  'ME-61741-view-slack-employee-tree': false,
  'ME-56701-view-old-truck-entry': false,
  'ME-56819-app-fullstory-enable': false,
  'ME-37245-view-allow-multiple-types-on-facility-address': false,
  'ME-61423-view-carrier-general-website-field': false,
  'ME-60304-view-vendor-route-financial-status-reason-codes-vendor-settlement':
    false,
  'ME-68766-feat-aardvark-records-v2-enabled': false,
  'ME-69768-view-carrier-record-release-method': false,
  'ME-62597-view-resold-imdl': false,
  'ME-64246-feat-load-city-state-stop': false,
  'ME-88901-feat-simplified-bookings-kebabs-on-offers-tab': false,
  'ME-53788-feat-refresh-ddt-minionrefreshevent': false,
  'ME-70314-feat-commodity-modal-enhancements': false,
  'ME-62401-feat-use-electronic-trackings': false,
  'ME-41503-feat-shipment-mvp': false,
  'ME-62618-app-invoke-availableRoute-query': false,
  'ME-84078-view-simplified-bookings-route-drawer': false,
  'ME-88898-feat-simplified-bookings-kebabs-on-capacity-manager': false,
  'ME-86013-feat-simplified-bookings-kebabs-on-procure-tab': false,
  'ME-85141-view-commitments-columns-on-route-board-pending-tab': false,
  'ME-85144-view-commitments-columns-on-customer-operations-pending-tab': false,
  'ME-72308-feat-use-geographies-api-for-autocomplete': false,
  'ME-85430-view-hide-front-page-offers': false,
  'ME-85427-view-offers-tab-not-default': false,
  'ME-73860-view-customer-commitment-surge': false,
  'ME-91969-view-orders-associated-to-lane': false,
  'ME-91969-view-routes-associated-to-lane': false,
} as const;
